import React, { useState, useEffect } from 'react';
import axios from 'axios';

function CaissePage({ apiUrl, auth }) {
  const today = new Date().toISOString().split('T')[0];
  const [mode, setMode] = useState('day'); // 'day' or 'range'
  const [selectedDate, setSelectedDate] = useState(today);
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [rentals, setRentals] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadData(); }, [selectedDate, dateFrom, dateTo, mode]);

  const loadData = async () => {
    setLoading(true);
    try {
      let params;
      if (mode === 'day') {
        params = { date: selectedDate };
      } else {
        params = { from: dateFrom, to: dateTo };
      }
      const [rentalsRes, expensesRes] = await Promise.all([
        axios.get(`${apiUrl}/rentals`, { params }),
        axios.get(`${apiUrl}/expenses`, { params })
      ]);
      setRentals(rentalsRes.data);
      setExpenses(expensesRes.data);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const totalRevenue = rentals.reduce((sum, r) => sum + r.total, 0);
  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);
  const profit = totalRevenue - totalExpenses;

  const goDay = (offset) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + offset);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const isToday = selectedDate === today;

  const periodLabel = mode === 'day'
    ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : `Du ${new Date(dateFrom + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} au ${new Date(dateTo + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`;

  const periodSuffix = mode === 'day' ? 'du jour' : 'de la période';

  // Count items sold
  const itemsSold = {};
  rentals.forEach(r => {
    r.items.forEach(item => {
      if (!itemsSold[item.name]) itemsSold[item.name] = { name: item.name, qty: 0, revenue: 0 };
      itemsSold[item.name].qty += item.qty;
      itemsSold[item.name].revenue += item.price * item.qty;
    });
  });
  const itemsList = Object.values(itemsSold).sort((a, b) => b.revenue - a.revenue);

  return (
    <div>
      <div className="page-header">
        <h1>🏦 Caisse</h1>
      </div>

      {/* Mode toggle + date controls */}
      <div style={{
        background: 'white', padding: '16px 20px', borderRadius: 'var(--radius)',
        boxShadow: 'var(--shadow)', marginBottom: '24px'
      }}>
        {/* Mode buttons */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
          <button
            className={`btn btn-sm ${mode === 'day' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setMode('day')}
          >📅 Jour</button>
          <button
            className={`btn btn-sm ${mode === 'range' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setMode('range')}
          >📆 Période</button>
        </div>

        {mode === 'day' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <button className="btn btn-outline btn-sm" onClick={() => goDay(-1)}>←</button>
            <input
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '14px', maxWidth: '160px' }}
            />
            <button className="btn btn-outline btn-sm" onClick={() => goDay(1)} disabled={isToday}>→</button>
            {!isToday && (
              <button className="btn btn-primary btn-sm" onClick={() => setSelectedDate(today)}>Aujourd'hui</button>
            )}
            <span style={{ marginLeft: 'auto', fontWeight: '600', color: 'var(--text-secondary)', fontSize: '14px', textTransform: 'capitalize' }}>
              {periodLabel}
            </span>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)' }}>Du</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '14px', maxWidth: '160px' }}
            />
            <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)' }}>Au</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '14px', maxWidth: '160px' }}
            />
            <button className="btn btn-outline btn-sm" onClick={() => {
              // This week
              const now = new Date();
              const day = now.getDay();
              const diff = now.getDate() - day + (day === 0 ? -6 : 1);
              const monday = new Date(now.setDate(diff));
              setDateFrom(monday.toISOString().split('T')[0]);
              setDateTo(today);
            }}>Cette semaine</button>
            <button className="btn btn-outline btn-sm" onClick={() => {
              // This month
              const now = new Date();
              setDateFrom(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]);
              setDateTo(today);
            }}>Ce mois</button>
            <span style={{ marginLeft: 'auto', fontWeight: '600', color: 'var(--text-secondary)', fontSize: '14px' }}>
              {periodLabel}
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="empty-state"><div className="emoji">⏳</div><div className="text">Chargement...</div></div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="stats-grid">
            <div className="stat-card revenue">
              <div className="stat-label">Recettes {periodSuffix}</div>
              <div className="stat-value">{totalRevenue.toFixed(0)} DA</div>
            </div>
            <div className="stat-card expense">
              <div className="stat-label">Dépenses {periodSuffix}</div>
              <div className="stat-value">{totalExpenses.toFixed(0)} DA</div>
            </div>
            <div className="stat-card profit">
              <div className="stat-label">Bénéfice net</div>
              <div className="stat-value" style={{ color: profit >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                {profit.toFixed(0)} DA
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Nombre de locations</div>
              <div className="stat-value">{rentals.length}</div>
            </div>
          </div>

          {/* Products breakdown + Expenses */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginTop: '20px' }}>
            <div className="table-wrapper">
              <div className="table-header">
                <h2>📦 Détail des ventes</h2>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th>Quantité</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {itemsList.length === 0 ? (
                    <tr><td colSpan="3" style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>Aucune vente</td></tr>
                  ) : (
                    itemsList.map(item => (
                      <tr key={item.name}>
                        <td style={{ fontWeight: '600' }}>{item.name}</td>
                        <td>{item.qty}</td>
                        <td style={{ fontWeight: '700', color: 'var(--success)' }}>{item.revenue.toFixed(0)} DA</td>
                      </tr>
                    ))
                  )}
                  {itemsList.length > 0 && (
                    <tr style={{ background: '#F0F7FF' }}>
                      <td style={{ fontWeight: '800' }}>Total</td>
                      <td style={{ fontWeight: '700' }}>{itemsList.reduce((s, i) => s + i.qty, 0)}</td>
                      <td style={{ fontWeight: '800', color: 'var(--success)' }}>{totalRevenue.toFixed(0)} DA</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="table-wrapper">
              <div className="table-header">
                <h2>💸 Dépenses</h2>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Montant</th>
                    <th>Date/Heure</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.length === 0 ? (
                    <tr><td colSpan="3" style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>Aucune dépense</td></tr>
                  ) : (
                    expenses.map(expense => (
                      <tr key={expense.id}>
                        <td style={{ fontWeight: '600' }}>{expense.description}</td>
                        <td style={{ fontWeight: '700', color: 'var(--danger)' }}>-{expense.amount.toFixed(0)} DA</td>
                        <td style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                          {expense.created_at ? new Date(expense.created_at).toLocaleDateString('fr-FR', {
                            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                          }) : '-'}
                        </td>
                      </tr>
                    ))
                  )}
                  {expenses.length > 0 && (
                    <tr style={{ background: '#FFF5F5' }}>
                      <td style={{ fontWeight: '800' }}>Total</td>
                      <td style={{ fontWeight: '800', color: 'var(--danger)' }}>-{totalExpenses.toFixed(0)} DA</td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Rentals list */}
          <div className="table-wrapper" style={{ marginTop: '20px' }}>
            <div className="table-header">
              <h2>🧾 Toutes les locations</h2>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th>Articles</th>
                  <th>Total</th>
                  <th>Employé</th>
                  <th>Date/Heure</th>
                </tr>
              </thead>
              <tbody>
                {rentals.length === 0 ? (
                  <tr><td colSpan="5" style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>Aucune location</td></tr>
                ) : (
                  rentals.map(rental => (
                    <tr key={rental.id}>
                      <td><span className="badge badge-info">#{rental.ticket_number}</span></td>
                      <td>{rental.items.map(i => `${i.name} x${i.qty}`).join(', ')}</td>
                      <td style={{ fontWeight: '700', color: 'var(--success)' }}>{rental.total.toFixed(0)} DA</td>
                      <td>{rental.employee_name || '-'}</td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                        {rental.created_at ? new Date(rental.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                        }) : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default CaissePage;
